import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Link,
} from "@mui/material";
import React from "react";
import {
  InfoFiltersSelectedData,
  MenuItemsOrderBy,
  ORDER_BY_TYPE,
  SelectedRows,
} from "../../../core/models/bills/bills.model";
import "./BillsOrderByBlock.scss";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../utils/formatters/formatters.util";

export interface BillsOrderByBlockProps {
  selectedRows: SelectedRows[];
  orderByDate?: ORDER_BY_TYPE;
  setOrderByDate: (value: ORDER_BY_TYPE) => void;
  totalRegistersQuery: number;
  totalFiltersApplied: number;
  isDisabled: boolean;
  handleDeleteAll: () => void;
  isTableLoading: boolean;
  showNoResults: boolean;
}

const BillsOrderByBlock = ({
  selectedRows,
  orderByDate,
  setOrderByDate,
  totalRegistersQuery,
  totalFiltersApplied,
  isDisabled,
  handleDeleteAll,
  isTableLoading,
  showNoResults,
}: BillsOrderByBlockProps) => {
  const { t } = useTranslation();
  const showDeleteAllButton = !!totalFiltersApplied || !!selectedRows.length;
  const infoFiltersSelectedData: InfoFiltersSelectedData[] = [
    {
      id: "results",
      condition: true,
      value: isTableLoading || showNoResults ? 0 : totalRegistersQuery,
      label: totalRegistersQuery === 1 ? "Resultado" : "Resultados",
    },
    {
      id: "appliedFilters",
      condition: !!totalFiltersApplied,
      value: totalFiltersApplied,
      label:
        totalFiltersApplied === 1 ? "Filtro aplicado" : "Filtros aplicados",
    },
    {
      id: "selectedRows",
      condition: !!selectedRows.length,
      value: selectedRows.length,
      label:
        selectedRows.length === 1
          ? "Factura seleccionada"
          : "Facturas seleccionadas",
    },
  ];

  const menuItemsOrderBy: MenuItemsOrderBy[] = [
    { id: "ascendingDate", value: "asc", label: "Fecha ascendente" },
    { id: "descendingDate", value: "desc", label: "Fecha descendente" },
  ];

  return (
    <Box component="div" className="bills-order-by-block">
      <Box component="div" display={"flex"} gap={"1rem"} flexWrap={"wrap"}>
        {infoFiltersSelectedData.map(
          ({ id, condition, value, label }, indexNumber) =>
            condition && (
              <React.Fragment key={id}>
                {!!indexNumber && <span>|</span>}
                <Typography variant="subtitle2" component="p">{`${formatNumber(
                  value
                )} ${t(`Facturas Página.${label}`)}`}</Typography>
              </React.Fragment>
            )
        )}
        {showDeleteAllButton && (
          <Link
            component="button"
            onClick={() => {
              handleDeleteAll();
            }}
          >
            {t("Tablas.Borrar todo")}
          </Link>
        )}
      </Box>
      <FormControl
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "1rem",
        }}
        className="bills-order-by-block__select"
      >
        <FormLabel htmlFor="order-by">
          {t("Facturas Página.Odenar por")}:
        </FormLabel>
        <Select
          sx={{ minWidth: "10rem" }}
          inputProps={{
            id: "order-by",
          }}
          value={orderByDate ?? undefined}
          onChange={(event: SelectChangeEvent<ORDER_BY_TYPE>) =>
            setOrderByDate(event.target.value as ORDER_BY_TYPE)
          }
          disabled={isDisabled}
          displayEmpty
          renderValue={(value) => {
            if (value) {
              const text = menuItemsOrderBy.find(
                ({ value: valueItem }) => valueItem === value
              )!.label;

              return t(`Facturas Página.${text}`);
            }

            return (
              <span style={{ color: "#A7B8BF" }}>
                {t(`Facturas Página.Ordenar por fecha`)}
              </span>
            );
          }}
        >
          {menuItemsOrderBy.map(({ id, value, label }) => (
            <MenuItem key={id} value={value}>
              {t(`Facturas Página.${label}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default BillsOrderByBlock;
